import {
  AutomationStep,
  FailureHandlerStep,
  LocalSubflowCallStepWithChildren,
} from '@/types/automationSteps.ts';
import { RunAutomationVersion } from '@/types/automations.ts';
import { StepType } from '@/types/automationFields.ts';
import { getAllStepChildren, isStepWithChildren } from '@/utils/stepUtils.ts';

const presentationStepsByVersion = new WeakMap<RunAutomationVersion, AutomationStep[]>();
const runtimeStepIdsByVersion = new WeakMap<RunAutomationVersion, Set<string>>();

export const buildRunPresentationSteps = (
  version: RunAutomationVersion,
): AutomationStep[] => {
  const cached = presentationStepsByVersion.get(version);
  if (cached) return cached;

  const steps = projectSteps(version.steps, version);
  presentationStepsByVersion.set(version, steps);
  return steps;
};

export const getLocalSubflowRuntimeStepIds = (
  version: RunAutomationVersion | undefined,
): Set<string> => {
  if (!version) return new Set();
  const cached = runtimeStepIdsByVersion.get(version);
  if (cached) return cached;

  const runtimeStepIds = new Set<string>();
  Object.values(version.local_subflow_execution_steps_by_call_step_id).forEach((steps) => {
    collectStepIds(steps, runtimeStepIds);
  });
  runtimeStepIdsByVersion.set(version, runtimeStepIds);
  return runtimeStepIds;
};

export const isLocalSubflowRuntimeStep = (
  version: RunAutomationVersion | undefined,
  stepId: string | undefined,
): boolean => !!stepId && getLocalSubflowRuntimeStepIds(version).has(stepId);

const projectSteps = (
  steps: AutomationStep[],
  version: RunAutomationVersion,
): AutomationStep[] => steps.map((step) => projectStep(step, version));

const projectStep = (
  step: AutomationStep,
  version: RunAutomationVersion,
): AutomationStep => {
  if (step.step_type === StepType.LocalSubflowCall) {
    const presentedStep: LocalSubflowCallStepWithChildren = {
      ...step,
      children: projectSteps(getLocalSubflowExecutionSteps(step.step_id, version), version),
    };
    return presentedStep;
  }
  if (step.step_type === StepType.Conditional) {
    return {
      ...step,
      conditions: step.conditions.map((condition) => ({
        ...condition,
        children: projectSteps(condition.children, version),
      })),
    };
  }
  if (step.step_type === StepType.FailureHandler) {
    return {
      ...step,
      children: projectSteps(step.children, version),
      handler_children: projectSteps(step.handler_children, version),
    } satisfies FailureHandlerStep;
  }
  if (
    step.step_type === StepType.Iterate ||
    step.step_type === StepType.Repeat ||
    step.step_type === StepType.Retry
  ) {
    return {
      ...step,
      children: projectSteps(step.children, version),
    };
  }
  return step;
};

const getLocalSubflowExecutionSteps = (
  callStepId: string,
  version: RunAutomationVersion,
): AutomationStep[] => {
  const executionStepsByCallStepId = version.local_subflow_execution_steps_by_call_step_id;
  if (!Object.hasOwn(executionStepsByCallStepId, callStepId)) {
    throw new Error(
      `Run automation version is missing Local Subflow execution steps for call ${callStepId}`,
    );
  }
  return executionStepsByCallStepId[callStepId];
};

const collectStepIds = (steps: AutomationStep[], target: Set<string>): void => {
  steps.forEach((step) => {
    target.add(step.step_id);
    if (isStepWithChildren(step)) {
      collectStepIds(getAllStepChildren(step), target);
    }
  });
};
